<script setup lang="ts">
import {computed, onBeforeMount, onMounted, ref, watch} from 'vue';
import {useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import TodayBentoGrid from '@/vue/organisms/today/today-bento-grid.vue';
import TodayAssessmentTile from '@/vue/organisms/today/today-assessment-tile.vue';
import TodayTasksTile from '@/vue/organisms/today/today-tasks-tile.vue';
import TodayUpcomingTile from '@/vue/organisms/today/today-upcoming-tile.vue';
import TodayExploreTile from '@/vue/organisms/today/today-explore-tile.vue';
import TodayContentTile from '@/vue/organisms/today/today-content-tile.vue';
import JournalTile from '@/vue/organisms/today/today-journal-tile.vue';
import ProgramTile from '@/vue/organisms/today/today-program-tile.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {useJournalTile} from '@/ts/composables/stateful/use-journal-tile';
import {availableCatalogues, mapTodayPrograms} from '@/ts/composables/pure/use-today-program-mapper';
import type {MoodDTO} from '@/ts/types/dto/journals-dto';
import {buildTodayRows} from '@/ts/composables/pure/use-today-layout';
import {resolveExploreTileState} from '@/ts/composables/pure/use-explore-tile-state';
import {useExploreRingCovers} from '@/ts/composables/stateful/use-explore-ring-covers';
import {useMediaQuery} from '@/ts/composables/stateful/use-media-query';
import {useExploreRingReveal} from '@/ts/composables/stateful/use-explore-ring-reveal';
import {useTodayAnalytics} from '@/ts/composables/stateful/use-today-analytics';
import {useTodayTaskCompletion} from '@/ts/composables/stateful/use-today-task-completion';
import {
  ANALYTICS_CATALOGUE_NAME,
  ANALYTICS_PROMO_NAME,
  ANALYTICS_TARGET_TYPE,
  resolveTileReport,
  TODAY_TARGET_EXPLORE,
  TODAY_TARGET_QUESTIONNAIRE,
  type TodayClick,
} from '@/ts/composables/pure/use-today-events';
import {mapTodayUpcoming} from '@/ts/composables/pure/use-today-upcoming-mapper';
import {mapTodayTasks} from '@/ts/composables/pure/use-today-tasks-mapper';
import {mapTodayContent} from '@/ts/composables/pure/use-today-content-mapper';
import {mapTodayAssessment} from '@/ts/composables/pure/use-today-assessment-mapper';
import {useTodayPayload} from '@/ts/composables/stateful/use-today-payload';
import {useTodayQuestionnaire} from '@/ts/composables/stateful/use-today-questionnaire';
import QuestionnaireStandardModal from '@/vue/molecules/components/questionnaire/questionnaire-standard-modal.vue';
import {useViewedContentStore} from '@/ts/store/viewed-content-store';
import LoadingError from '@/vue/templates/loading-error.vue';
import {
  type JournalEntryDraft,
  type TodayAssessmentSlide,
  type TodayCardItem,
  TodayCardKind,
  type ProgramCatalogue,
  type TileProgram,
  type TodayContent,
  type TodayOnboardingQuestionnaire,
  TodayTasksFace,
  TodayTileKind,
} from '@/ts/types/component/today-page';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import {useFeatureAccess} from '@/ts/composables/stateful/use-feature-access';
import {isContractExpired} from '@/ts/composables/stateful/use-contract-expiry-guard';
import {useUserStore} from '@/ts/store/user-store';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {MetricAction, MetricCategory} from '@/ts/types/dto/metric-dto';
import {pagesPath} from '@/ts/router/pages-path';
import {useSnackbarStore} from '@/ts/store/snackbar-store';

const router = useRouter();
const {t} = useI18n();
const snackbarStore = useSnackbarStore();
const {canAccessExplore, canAccessHome} = useFeatureAccess();
const api = useApi();
const logger = useLogger();
const userStore = useUserStore();
const {payload, loading, error, load} = useTodayPayload();
const {hasViewed} = useViewedContentStore();

// The greeting name comes from the payload rather than from the user store, and it is the same
// full name the page this one replaced greeted with: the backend resolves it next to the greeting
// window it also derives from the timezone, so the two cannot disagree about who is being greeted.
const userName = computed<string>(() => payload.value?.userName ?? '');

/**
 * The storage key that records this browser having seen the home page, written by the page this one
 * replaces. Kept under its old name on purpose: renaming it would tell the metric that every
 * existing user is visiting home for the first time.
 */
const HOME_VIEWED_KEY = 'viewed_today_page';

/**
 * The tiles of the discover row, all three of which lead into Explore.
 *
 * A licence can blacklist EXPLORE_SECTION, and when it does the shell already hides the Explore
 * tab and both destinations bounce the user to their interventions on mount (explore-page.vue,
 * block-content-page.vue). Rendering the row for those users would put three dead ends in the
 * middle of the bento, so the tiles are dropped instead — and since the row holds nothing else,
 * `buildTodayRows` drops the band with them.
 */
const EXPLORE_TILES: readonly TodayTileKind[] = [
  TodayTileKind.explore,
  TodayTileKind.contentPrimary,
  TodayTileKind.contentSecondary,
];

// This is the home route, so the licence check below is the only thing standing between a user
// whose licence does not include the home section and the first page they would otherwise land on.
// It was here before the route moved and it means more now, not less.
onBeforeMount(() => {
  if (!canAccessHome.value) {
    void router.replace(pagesPath.intervention.interventionsList);
  }
});

/**
 * The page's view-models, one per tile, each the output of its own pure mapper over the slice of
 * the payload that belongs to it. The mappers decide every face; this page decides nothing about
 * what a tile shows — it only hands each one what the payload said.
 *
 * All of them read `payload` and nothing else, so there is a single moment when the page fills:
 * the request resolves and every tile arrives together.
 */
const upcoming = computed(() => (payload.value ? mapTodayUpcoming(payload.value.upcoming) : null));

// Null is a face-less payload, not an empty tile: the mapper returns it for a shape it cannot
// vouch for (a VIDEO with no video, a list whose every task is unflattenable), and the honest
// answer to that is no tile rather than a claim the payload does not support. The cell goes with
// it, and the row reflows on its own.
const tasks = computed(() => (payload.value ? mapTodayTasks(payload.value.tasks) : null));

/**
 * The content pair, at whatever length the backend fixed it: two normally, one when a pinned piece
 * disappeared from Directus mid-window, none when the pool is empty. Whatever is missing drops its
 * cell instead of rendering an empty card.
 *
 * Called INSIDE the computed on purpose, and `hasViewed` with it. The store's set is what makes a
 * piece opened during this visit show its marker on the way back, and that only works while
 * reading it registers as a dependency here — assigning this once in a handler compiles, passes
 * every test, and quietly restores the bug IPD-3035 fixed.
 */
const content = computed(() => (payload.value ? mapTodayContent(payload.value.content, hasViewed) : []));

// The countdowns are measured when the payload lands rather than on a clock the page keeps: the
// mapper turns each unlock date into a duration once, and the copy speaks in days.
const assessment = computed(() => (payload.value ? mapTodayAssessment(payload.value.assessment, new Date()) : null));

/**
 * The program tile's two halves: the rows the user has, and which groups the tile may invite them
 * into — which the rows cannot say, since a licence can offer a group they joined nothing of.
 *
 * Up here rather than beside the journal below because the row builder reads both: a tile with no
 * rows and no catalogue has nothing to draw and leaves no cell.
 *
 * The pre-payload answer never reaches the screen (the bento waits on the payload, and the payload
 * is only ever assigned on success). It matches the tile's own defaults all the same.
 */
const programs = computed(() => (payload.value ? mapTodayPrograms(payload.value.program) : []));
const catalogues = computed(() => (payload.value
  ? availableCatalogues(payload.value.program)
  : {medical: true, prevention: false}));

/**
 * Which tiles the bento draws. Five independent reasons a kind is left out, and every one of them
 * means the same thing to the grid: no cell, and a row with nothing left in it disappears.
 *
 *  - a licence that does not reach Explore takes the whole discover row with it
 *  - an assessment tile the payload did not send (nothing recommended, nothing that ever reopens,
 *    nothing due for more than three days — all three arrive here as no tile, and the journal
 *    expands across the row)
 *  - a tasks tile whose payload no mapper can vouch for
 *  - a content pair that came back short, which drops the cell it no longer fills
 *  - a program tile with no programs of the user's own and no catalogue to send them browsing
 *
 * The licence check is the only one that is not about the payload, and it stays that way: it is
 * about what the user may reach, not about what they have to do today.
 */
const visibleKinds = computed(() => Object.values(TodayTileKind)
  .filter((kind) => canAccessExplore.value || !EXPLORE_TILES.includes(kind))
  .filter((kind) => kind !== TodayTileKind.assessment || assessment.value !== null)
  .filter((kind) => kind !== TodayTileKind.taskList || tasks.value !== null)
  .filter((kind) => kind !== TodayTileKind.contentPrimary || content.value.length > 0)
  .filter((kind) => kind !== TodayTileKind.contentSecondary || content.value.length > 1)
  // A licence with nothing in either group and no program of the user's own leaves this tile with
  // nothing to say: no rows to show and nowhere to send anyone. Drawn anyway it is a heading over
  // "start your first path" with not one link under it, which is worse than the gap.
  .filter((kind) => kind !== TodayTileKind.programs
    || programs.value.length > 0 || catalogues.value.medical || catalogues.value.prevention));
/**
 * Which face the Explore tile shows, and whether it is given the whole discover row for it
 * (IPD-3256). Both answers come from the same two dates the payload carries, so they cannot
 * disagree: the card that announces new content is the card that has room to show it.
 */
const exploreState = computed(() => resolveExploreTileState({
  assessments: payload.value?.assessment?.items ?? [],
  contentPinnedAt: payload.value?.content?.pinnedAt,
  assessmentAnswered: payload.value?.checklist?.assessmentAnswered,
  lastAssessmentAnsweredAt: payload.value?.lastAssessmentAnsweredAt,
}));

/**
 * Below this there is nothing to fetch and nothing to wait for: the ring cannot be drawn (IPD-3270).
 *
 * A floor rather than the container query the stylesheet asks, which is about the card and not the
 * window. The card is the window less the page's own gutters, so the 940 of card content the ring
 * needs is about 1072 of window — and anything that narrows the card further, a sidebar say, only
 * makes this err towards fetching pictures that go undrawn. It never misses a ring.
 */
const ringFitsViewport = useMediaQuery('(min-width: 1072px)');

// Whether there are pictures to go and get: a face that draws a ring, on a window that can hold
// one. It says nothing about the row, whose shape is the payload's answer and the stylesheet's.
const exploreNeedsCovers = computed<boolean>(() => (
  ringFitsViewport.value && (exploreState.value.firstUse || exploreState.value.hasNewContent)
));

// The covers on it, which are the reader's own recommendations.
const {
  covers: exploreRingCovers,
  settled: exploreCoversSettled,
  drawn: exploreCoversDrawn,
  prepare: prepareExploreRingCovers,
  refreshArrival: refreshExploreArrival,
} = useExploreRingCovers(() => exploreNeedsCovers.value, () => canAccessExplore.value);

// The face the card draws, which is the payload's except while a change the reader caused waits
// for the picture that announces it.
const {face: exploreFace, expectAfterAnswer: exploreRingFollowsAnswer} = useExploreRingReveal(
  () => exploreState.value,
  () => exploreCoversSettled.value,
);

const exploreShowsRing = computed<boolean>(() => exploreFace.value.firstUse || exploreFace.value.hasNewContent);

// Only the first draw waits. Once the ring has been drawn it keeps what it has through a later
// read — the one that chooses the cover to drop in — rather than coming down while that runs.
const exploreCoversPending = computed<boolean>(() => !exploreCoversDrawn.value);

// The page is not ready while the ring's first pictures are still coming (IPD-3270). The bento has
// one loading state for everything on it, and the alternative to using it here is drawing the card
// without its ring and filling it in afterwards, which is the loading the design asked us to stop
// showing.
//
// The FIRST pictures, and only those. The band is read again when the reader answers, and a page
// that went blank for that would take the bento out from under them — there is a card on screen
// for that change, and it stays until the new one can be drawn whole.
const exploreCoversLoading = computed<boolean>(() => exploreNeedsCovers.value && exploreCoversPending.value);

// The whole row is the room the ring needs, so a window too narrow to draw one does not give it
// away (IPD-3270): the card would be four hundred pixels of nothing between its copy and its
// button, and the design's own reading of a small screen has this tile beside the two content
// cards rather than above them.
const rows = computed(() => buildTodayRows(visibleKinds.value, {
  exploreTakesRow: exploreShowsRing.value && ringFitsViewport.value,
}));

// Declared after `rows`, which it reads: the getter means every event describes the page as it is
// when it fires, not as it was when the page was set up.
const analytics = useTodayAnalytics(() => rows.value);
const taskCompletion = useTodayTaskCompletion();

/**
 * The analytics visit (IPD-2974 8.2), reported when the page has tiles to report rather than at
 * mount: the event carries which ones this user was given, and at mount there is no payload yet.
 *
 * After the flush, so the tiles it names are the ones actually on screen. A visit whose payload
 * never arrives raises nothing, which keeps it usable as the denominator of the tile events — the
 * page was not shown. That is why it is not next to `reportView` above, which counts a redirected
 * user on purpose: that call continues the old home page's series and has to stay comparable with
 * it, while this one starts its own.
 */
watch(payload, (loaded) => {
  if (loaded) {
    analytics.pageViewed();
  }
}, {flush: 'post'});

/**
 * What the tiles that draw more than one thing are drawing, for the event that reports a tile as
 * seen. Read at that moment rather than remembered, so a tile whose payload settled late reports
 * what was actually on screen.
 *
 * The promo rides along because the tasks tile's promo face is three different offers, and which
 * one was put in front of the user is the only thing that face is asked about.
 */
const tileFaces = computed(() => ({
  upcoming: upcoming.value?.face ?? null,
  tasks: tasks.value?.face ?? null,
  assessment: assessment.value?.face ?? null,
  promo: tasks.value?.face === TodayTasksFace.promo ? tasks.value.promo : null,
}));

/**
 * Which piece of reading sits in each of the two content cells, so a cell coming on screen can
 * report the piece it was holding.
 */
const contentByTile = computed<Partial<Record<TodayTileKind, TodayContent>>>(() => ({
  [TodayTileKind.contentPrimary]: content.value[0],
  [TodayTileKind.contentSecondary]: content.value[1],
}));

function onTileSeen(kind: TodayTileKind): void {
  analytics.tileViewed(kind, resolveTileReport(kind, tileFaces.value));

  // A content cell reports the piece it was offering as well as itself: "shown" has to mean the
  // card reached the screen, and both cells sit below the fold on a phone, so counting them at
  // load would count as offered what nobody scrolled to.
  const piece = contentByTile.value[kind];
  if (piece) {
    analytics.contentShown(kind, piece);
  }
}

/**
 * The questionnaire overlay, which three tiles ask for and none of them opens: a questionnaire is a
 * modal in this app rather than a route, so the tiles report the intent and the page owns the
 * overlay. Each of them sends the id, and whether the card it came from was one the user may still
 * answer.
 *
 * WHY THE CARD DECIDES AND NOT THE QUESTIONNAIRE: the one flag the questionnaire carries,
 * `isAnswered`, says the user has answered it at some point — the endpoint reads their answers with
 * no time limit on them — and an assessment off its cooldown is exactly that: answered before, and
 * offered again on purpose. Deciding on it would leave the reassessment the tile just offered
 * impossible to take. What separates the two cases is the card: the tile gives a call to action
 * only to the one that is open, and the task list marks the ones already done today (IPD-3087).
 */
const questionnaire = useTodayQuestionnaire();
// Answering is what puts new content on the tile, so the covers are downloaded while the reader is
// still in the questionnaire. Waiting for the refreshed payload to ask for them puts the answer,
// the refresh and ten pictures end to end, and the card sits still through all three.
watch(() => questionnaire.questionnaire.value, (open) => {
  if (open) {
    prepareExploreRingCovers();
  }
});

function openQuestionnaireFor(task: TodayCardItem): void {
  if (task.kind !== TodayCardKind.assessment) {
    return;
  }
  // The prefix is the mapper's, which synthesises ids per kind because the four id spaces are
  // unrelated; what the questionnaire endpoint takes is the bare one.
  //
  // A row the list marks as done is one the user answered today, and this list keeps those rather
  // than dropping them so it does not reshuffle underfoot. Opened read-only: it is there to be read
  // back, and a completed questionnaire must not be modified (IPD-2772). The backend would take a
  // second set of answers for it and file them as another pass, which is not what a row saying
  // "done" is offering.
  void questionnaire.open(task.id.replace(/^assessment:/u, '') as UuidDTO, task.done);
}

function reportClick(kind: TodayTileKind, click: TodayClick): void {
  analytics.tileClicked(kind, resolveTileReport(kind, tileFaces.value), click);
}

/**
 * A card was activated, in whichever of the two tiles draws them.
 *
 * The tile has to be passed in: the shared card reports the same way from the Upcoming tile and
 * from a row of the task list, and which one it was is half the question.
 */
function onCardSelected(kind: TodayTileKind, item: TodayCardItem): void {
  reportClick(kind, {
    ctaId: 'card',
    targetType: ANALYTICS_TARGET_TYPE[item.kind],
    targetId: item.id,
  });
  openQuestionnaireFor(item);
  // Written where the click is counted, so the screen that says the task is done can attribute it
  // to this page. The mark travels in the tab's own storage because a query cannot: it would not
  // survive the links built between the pages of a session, and the completion is on the last one.
  //
  // Sessions only, and after the overlay rather than before it. One slot holds the mark and the
  // session-completed screen is the only one that redeems it, so marking a homework, challenge or
  // assessment could never report anything and would evict a session mark that could.
  if (item.kind === TodayCardKind.session) {
    taskCompletion.markOpened(item.id);
  }
}

/**
 * A shortcut to Explore was taken, from the tile that is only that or from the Upcoming tile's
 * nudge on the face that has nothing of the user's own to offer.
 *
 * No target id: Explore is a destination with nothing to identify, and the event still says the
 * page sent someone there.
 */
function onBrowseExplore(kind: TodayTileKind): void {
  reportClick(kind, {ctaId: 'browse', targetType: TODAY_TARGET_EXPLORE, targetId: null});
}

/** A program of the user's own was opened from the programs tile. */
function onProgramOpened(program: TileProgram): void {
  reportClick(TodayTileKind.programs, {
    ctaId: 'program',
    targetType: ANALYTICS_TARGET_TYPE[TodayCardKind.program],
    targetId: program.interventionId,
  });
}

/**
 * One of the two catalogues was browsed, whether from an empty block or from a first-use card.
 *
 * Reported as the catalogue rather than as where it led: medical programs are browsed from Paths
 * and prevention ones from Explore, and a route the app can change should not be what the data
 * says happened.
 */
function onCatalogueBrowsed(catalogue: ProgramCatalogue): void {
  reportClick(TodayTileKind.programs, {
    ctaId: 'browse',
    targetType: ANALYTICS_CATALOGUE_NAME[catalogue],
    targetId: null,
  });
}

/**
 * The promo's CTA, which is the same act as the invitation above from the other tile: go and
 * browse a category. Reported with the same vocabulary, so one question answers both, and the
 * tile says which of the two it came from.
 */
function onPromoBrowsed(): void {
  const {promo} = tileFaces.value;
  reportClick(TodayTileKind.taskList, {
    // Falls back to where the button actually goes, for a promo face the mapper could not name.
    // It builds the face from the payload's promo, so this is a fallback rather than a case.
    targetType: promo ? ANALYTICS_PROMO_NAME[promo] : TODAY_TARGET_EXPLORE,
    ctaId: 'browse',
    targetId: null,
  });
}

/**
 * A piece of reading was opened from one of the two content cells.
 *
 * This is what the ticket asked for as `today_content_opened`, folded into the click: it fires on
 * the same activation and all but two of its properties are the same, and the two it adds — what
 * the piece is and whether it was already read — ride along here.
 */
function onContentSelected(kind: TodayTileKind, piece: TodayContent): void {
  reportClick(kind, {
    ctaId: 'card',
    targetType: ANALYTICS_TARGET_TYPE[TodayCardKind.content],
    targetId: piece.id,
    media: piece.media,
    alreadyRead: piece.read,
  });
}

/**
 * The journal tile, which used to fetch for itself, as the program tile above did. Both read the
 * payload now, so the page is one request and every tile fills at the same moment instead of three
 * settling one after another.
 *
 * The journal keeps a call of its own, but not for the stack: the template carries the content id
 * an entry is saved against and the feelings the form offers, and the payload carries neither.
 */
const journal = useJournalTile(computed(() => payload.value?.journal.entries ?? []));
// Spelled out rather than `InstanceType<typeof JournalTile>`, which resolves to
// `any` here and would silently stop checking the call below.
const journalRef = ref<{confirmSaved(entry: {mood: MoodDTO; text: string}): void} | null>(null);

/**
 * Reports the visit, and marks the browser as having seen this page.
 *
 * A blocked `localStorage` THROWS rather than answering null — Safari in private mode, site data
 * turned off, a full quota — and this runs before the payload is asked for, so a throw escaping here
 * would leave the root as a header over an empty page with neither a spinner nor an error.
 *
 * Only the write is guarded against that, and the reason is in the comment on the read: a browser
 * that refuses to answer a read never reaches this page at all, because the router's own navigation
 * guard reads the stored session without a guard of its own before a route resolves. Refusing a
 * write while answering reads is the case that does arrive.
 *
 * A browser that cannot store is reported as a first visit every time, which is the honest answer:
 * nothing on it remembers otherwise.
 */
function reportView(): void {
  // Read without a guard, deliberately, where the write below has one: the two fail in different
  // browsers and only one of those browsers ever gets this far. A browser that throws on reading
  // dies in the router's own navigation guard, which reads the stored session unguarded before any
  // route resolves, so a catch here would be covering a case that cannot reach the page. Refusing
  // to write is the reachable one: reads are answered and the mark simply never sticks.
  const isFirstVisit = !localStorage.getItem(HOME_VIEWED_KEY);

  api.metric.logEvent({
    category: MetricCategory.TodayWebPage,
    action: MetricAction.View,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    payload: {is_first_visit: isFirstVisit},
  }).catch((err: unknown) => {
    // Reported rather than swallowed, and never raised: a page view that did not reach the backend
    // is not something to put in front of the user, and throwing here would abort the mount.
    logger.error('Failed to report the Today page view:', err)();
  });

  if (!isFirstVisit) {
    return;
  }
  try {
    localStorage.setItem(HOME_VIEWED_KEY, 'true');
  } catch {
    // Same again: a browser that will not keep the mark simply reports the next visit as first.
  }
}

onMounted(() => {
  // Reported before the gate below, and unconditionally, because that is what the page this one
  // replaces did: its licence check sat in `onBeforeMount` and only redirected, and contract expiry
  // it never looked at, since the shell locks those users centrally. Both of them counted towards
  // this metric, so gating it here as well would step the series down at cut-over — instrumentation
  // moving rather than anyone leaving, which is the one thing a page-view series must not do on the
  // day it changes hands.
  reportView();

  // Two ways a user reaches this page on their way out of it, and neither should ask the backend for
  // anything. The replace is async and the component mounts anyway, so both are checked here.
  //
  // It matters for the page's own request: GET /me/today fixes the content pair for the window and
  // can mark an expired challenge as failed, so firing it here would write to the state of someone
  // who is being sent elsewhere.
  //
  // The licence check is the one that was always here. The expiry check is not redundant with the
  // navigation guard: on a cold load the guard runs before `/me` has answered, so it lets the
  // navigation through and the lock arrives later from a watcher — in the same flush that mounts
  // this page. That was harmless while this lived at a debug-only address nobody loads cold. It is
  // the application's root now, which is the most cold-loaded URL there is.
  if (!canAccessHome.value || isContractExpired(userStore.userData)) {
    return;
  }

  void load();
  void journal.load();
});

function onStartOnboarding(onboarding: TodayOnboardingQuestionnaire): void {
  reportClick(TodayTileKind.upcoming, {
    ctaId: 'onboarding',
    targetType: TODAY_TARGET_QUESTIONNAIRE,
    targetId: onboarding.id,
  });
  void questionnaire.open(onboarding.id as UuidDTO);
}

function onAssessmentSelected(slide: TodayAssessmentSlide): void {
  reportClick(TodayTileKind.assessment, {
    ctaId: 'card',
    targetType: TODAY_TARGET_QUESTIONNAIRE,
    targetId: slide.id,
  });
  void questionnaire.open(slide.id as UuidDTO);
}

/**
 * Takes the overlay down, and refreshes the page if an answer went through it.
 *
 * Answering moves the user between states of this page — the onboarding one empties the Upcoming
 * tile's welcome face and opens the assessment slider — so the payload is stale once the overlay
 * closes. Reloaded here rather than on submit, so the bento is not blanked behind a modal the user
 * is still reading their score in, and only when an answer actually went through: the overlay
 * closes on its own control too, and a questionnaire opened and dismissed leaves the page exactly
 * as it was.
 *
 * Waited for rather than read: a questionnaire with no score, which is all of them in practice, has
 * the modal ask to submit and ask to close in the same tick, so at this point the answer has not
 * reached the backend yet.
 */
async function finishQuestionnaire(): Promise<void> {
  const submitted = await questionnaire.settle();
  questionnaire.close();
  if (submitted) {
    // Raised once the modal is gone, so it confirms over the page the reader is back on. A toast:
    // there is nothing to do about it.
    snackbarStore.showToast(t('snackbar_answers_saved'));
    // Before the payload, which is what tells the tile it has something new to say: the reveal has
    // to know the change is this reader's own before it happens, and the cover that drops into the
    // ring is chosen from what the platform has just learnt.
    const landed = load();
    exploreRingFollowsAnswer(landed);
    refreshExploreArrival();
    await landed;
  }
}

/** Whether the above is already running, since the overlay reports finishing more than once. */
let finishing = false;

/**
 * The modal reports finishing twice — through its own event, and through the dialog reporting
 * itself closed — and the wait inside `finishQuestionnaire` falls between the two, so without this
 * the second report would reload the page a second time.
 *
 * Split from the work it guards so the flag is only ever assigned synchronously: reset from inside
 * an async function, after the await, it is exactly the shape of a lost update.
 */
function onQuestionnaireFinished(): void {
  if (finishing) {
    return;
  }
  finishing = true;
  void finishQuestionnaire().finally(() => {
    finishing = false;
  });
}

/**
 * The tile emits a draft, not a saved entry: it is confirmed only once the
 * request succeeds, so a failure leaves the user's draft where they left it.
 */
async function onJournalSave(draft: JournalEntryDraft): Promise<void> {
  const saved = await journal.save(draft);
  if (saved) {
    journalRef.value?.confirmSaved({mood: draft.mood, text: draft.text});
    // After it is stored, not when the button was pressed: a failed save leaves the draft on
    // screen for another try, and counting that would count entries that do not exist.
    analytics.journalSaved({
      hasText: draft.text.trim().length > 0,
      feelingsCount: draft.feelingIds.length,
    });
  }
}
</script>
<template>
  <!-- The whole page is one request, so it has one loading state and one failure. `loading-error`
       is also what turns a consent or licence error code into its redirect instead of an error
       box, the same way every other page-level fetch in the app does. -->
  <loading-error id="today-page" type="page" :loading="loading || exploreCoversLoading" :error="error">
    <div class="today-page">
      <header class="page-header">
        <h1 class="title">
          {{ $t('home') }}
        </h1>
        <svg-illustration name="right-down-arrow.svg" inner-class="inner-right" class="arrow-right" aria-hidden="true"/>
      </header>
      <!-- No payload, no bento. Every tile is drawn from one, so rendering the grid before it
           arrives would put empty cells on screen — the one thing the composition is built to
           avoid. Reachable in two ways, both brief: the tick between mount and the request going
           out, and a user the access check is redirecting away, for whom it never goes out. -->
      <today-bento-grid v-if="payload" :rows="rows" @tile-seen="onTileSeen">
        <template v-if="upcoming" #upcoming>
          <today-upcoming-tile
            :upcoming="upcoming"
            :user-name="userName"
            @start-onboarding="onStartOnboarding"
            @select-item="(item) => onCardSelected(TodayTileKind.upcoming, item)"
            @browse="onBrowseExplore(TodayTileKind.upcoming)"
          />
        </template>
        <!-- Every tile below is guarded the same way, and the guard is not defensive: a kind the
             payload could not fill is already out of `rows`, so the slot it would have gone in is
             never rendered. The `v-if` is what narrows the view-model for the prop. -->
        <template v-if="tasks" #taskList>
          <today-tasks-tile
            :tile="tasks"
            @select-task="(item) => onCardSelected(TodayTileKind.taskList, item)"
            @browse="onPromoBrowsed"
            @video-watched="analytics.introWatched"
          />
        </template>
        <template #explore>
          <today-explore-tile
            :first-use="exploreFace.firstUse"
            :has-new-content="exploreFace.hasNewContent"
            :ring-tiles="exploreRingCovers.ringTiles"
            :arriving-tile="exploreRingCovers.arrivingTile"
            :covers-pending="exploreCoversPending"
            @browse="onBrowseExplore(TodayTileKind.explore)"
          />
        </template>
        <template v-if="content[0]" #contentPrimary>
          <today-content-tile
            :content="content[0]"
            @select="(item) => onContentSelected(TodayTileKind.contentPrimary, item)"
          />
        </template>
        <template v-if="content[1]" #contentSecondary>
          <today-content-tile
            :content="content[1]"
            @select="(item) => onContentSelected(TodayTileKind.contentSecondary, item)"
          />
        </template>
        <template v-if="assessment" #assessment>
          <today-assessment-tile :tile="assessment" @select="onAssessmentSelected"/>
        </template>
        <template #journal>
          <journal-tile
            ref="journalRef"
            :entries="journal.entries.value"
            :feelings-by-mood="journal.feelingsByMood.value"
            :default-feelings="journal.defaultFeelings.value"
            :loading="journal.loading.value"
            :saving="journal.saving.value"
            @save="onJournalSave"
          />
        </template>
        <template #programs>
          <program-tile
            :programs="programs"
            :has-medical-catalogue="catalogues.medical"
            :has-prevention-catalogue="catalogues.prevention"
            @open-program="onProgramOpened"
            @browse-catalogue="onCatalogueBrowsed"
          />
        </template>
      </today-bento-grid>

      <!-- Outside the grid: it is an overlay over the whole page, not something a cell holds. -->
      <questionnaire-standard-modal
        v-if="questionnaire.questionnaire.value"
        v-model="questionnaire.answers.value"
        :is-modal-open="true"
        :questionnaire="questionnaire.questionnaire.value"
        :loading="questionnaire.busy.value"
        :readonly="questionnaire.readonly.value"
        :submitted="questionnaire.answered.value"
        @update="questionnaire.submit"
        @update:is-modal-open="onQuestionnaireFinished"
        @finish="onQuestionnaireFinished"
      />
    </div>
  </loading-error>
</template>
<style scoped lang="scss">
// The design caps the content at 1480px and centres it, rather than letting it grow to the
// app-wide 1920px and sit against the left edge: past that width the bento would keep stretching
// its columns into tiles far wider than any of them were drawn. With the page padding, this
// leaves the bento itself at 1416px, which is what the prototype measures.
//
// Scoped to this page on purpose. `page-alignment` is shared by every screen, so widening the
// change would move Paths, Explore, Journal and the account pages too. If the redesign ends up
// wanting it everywhere, the cap and the centring belong in the mixin and this override goes.
//
// Two consequences of being the only centred page, both accepted rather than overlooked: moving
// between Today and any other screen shifts the content sideways on a wide window, and for an
// Allianz-branded user the branding footer — a sibling the shell pins to the right of the whole
// content area — no longer lines up with this page's right edge. Both resolve on their own the
// day the cap moves into the shared mixin; asked of design with the rest of the Today questions.
.today-page {
  @include page-alignment(1480px);

  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--Spacing-spacing-5);
  width: 100%;
  margin-inline: auto;
  background-color: var(--today-surface-page);
}

// The same header the rest of the app draws, so the page keeps the familiar title and arrow while
// the tiles below change. Deliberately described rather than pointed at: the page it was copied
// from is on its way out, and a reference that outlives its target is worse than none.
.page-header {
  display: flex;
  align-items: center;

  .title {
    @include font-h2-bold;

    margin: 0;
    color: var(--Neutral-UI-neutral-ui-100);
  }

  .arrow-right {
    margin-left: var(--Spacing-spacing-3);
  }
}
</style>
